import React, { useCallback, useEffect, useState } from 'react';
import {
  CircularProgress,
  Divider,
  Grid,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import TopBar from 'src/pages/sendout/common/TopBar';
import CustomError from 'src/pages/sendout/common/Error';
import { HandoverItemType } from 'src/types/handover';
import useAsync from 'src/hooks/useAsync';
import { useStyles } from 'src/pages/sendout/common/MainView';
import { useTranslation } from 'react-i18next';
import { getSendoutTranslation } from 'src/services/api/locales';
import { loadFallbackBundles, SENDOUT_NAMESPACE } from 'src/i18n';
import { toast as showToast } from 'react-toastify';
import { HandoverSummary } from 'src/pages/sendout/Generic/components/HandoverSummary';
import FormSelect from 'src/pages/sendout/Generic/components/FormSelect';
import 'src/pages/sendout/Generic/GenericMainView.css';
import OrderSuccessComponent, { DynamicFormData } from 'src/pages/sendout/Generic/OrderSuccessComponent';

dayjs.extend(utc);

interface Role {
  id: string;
  name: string;
  key: string;
}

export interface GenericSendoutParticipant {
  id: string;
  firstName: string;
  lastName: string;
  companyName: string | null;
  role: Role;
  email: string;
  phone: string;
}

export interface DynamicForm {
  id: string;
  name: string;
  description: string;
}

export interface PropertyHandover {
  handoverId: string;
  providerHandoverId: string;
  address: string;
  city: string;
  zipCode: string;
  region: string;
  date: string;
  dynamicForms: DynamicForm[];
  participants: GenericSendoutParticipant[];
}

export interface MainViewProps {
  getHandoverForSendoutView: (immediateParams: {
    itemType: HandoverItemType;
  }) => Promise<unknown>;
  orderForm: (body: {
    configId: string;
    handoverId: string;
  }) => Promise<{ form: DynamicFormData }>;
}

function GenericSendOutView({
  getHandoverForSendoutView,
  orderForm,
}: MainViewProps) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { classes } = useStyles({ isMobile })();
  const { i18n, t } = useTranslation(SENDOUT_NAMESPACE);

  const [locale, setLocale] = useState('no');
  const [selectedFormId, setSelectedFormId] = useState('');
  const [data, setData] = useState<PropertyHandover | undefined>();
  const [loading, setLoading] = useState(true);
  const [ordering, setOrdering] = useState(false);
  const [orderComplete, setOrderComplete] = useState(false);

  const [dynamicFormData, setDynamicFormData] = useState<DynamicFormData | null>(null);

  // Load translations whenever locale changes
  useEffect(() => {
    void Promise.all([
      getSendoutTranslation(locale),
      loadFallbackBundles(locale, [SENDOUT_NAMESPACE], getSendoutTranslation),
    ]).then(([translations]) => {
      i18n.addResourceBundle(locale, SENDOUT_NAMESPACE, translations);
      i18n.changeLanguage(locale);
    });
  }, [i18n, locale]);

  const onLoadSuccess = useCallback(
    (handover: PropertyHandover) => {
      setData(handover);
      setLoading(false);
    },
    [t],
  );

  const handleSubmit = async () => {
    if (!selectedFormId) return;
    setOrdering(true);

    orderForm({
      configId: selectedFormId,
      handoverId: data?.handoverId || '',
    })
      .then((data) => {
        setDynamicFormData(data.form);
        setOrderComplete(true);
      })
      .catch(() => {
        showToast.error(t('sendoutError'));
      })
      .finally(() => {
        setOrdering(false);
      });
  };

  const { error } = useAsync({
    fn: getHandoverForSendoutView,
    immediate: true,
    immediateParams: {},
    options: {
      successCb: onLoadSuccess,
      toast: { useToast: false },
    },
    deps: [],
  });

  if (error) {
    return <CustomError error={error} />;
  }

  return (
    <>
      <TopBar />
      <Divider className={classes.divider} />

      { !orderComplete && <Grid container className={classes.root}>
        {(loading || ordering) ? (
          <Grid item xs={12} className={classes.center}>
            <CircularProgress className={classes.spinnerColor} />
          </Grid>
        ) : (
          (data && !ordering) && (
            <div className="sendout-paper">
              <HandoverSummary data={data} />

              <div className="sendout-form-section">
                <FormSelect
                  options={data.dynamicForms}
                  onChange={(id: string) => setSelectedFormId(id)}
                />
              </div>

              <div className="sendout-submit-wrapper">
                <button
                  disabled={!selectedFormId}
                  className="sendout-submit-button"
                  onClick={handleSubmit}
                >
                  {t('buttons.send')}
                </button>
              </div>
            </div> )
        )}
      </Grid> }

      { data && orderComplete && !ordering && dynamicFormData && <OrderSuccessComponent dynamicFormData={dynamicFormData}/> }

    </>
  );
}

export default GenericSendOutView;